/**
 * POC contact helpers aligned with POST/PUT /billing/quotations API.
 * Fields: name, role, mobile, email, gstin (gst_number accepted as input alias).
 */

const trimString = value =>
  typeof value === 'string' ? value.trim() : '';

const normalizeGstin = (gstin, gstNumber) => {
  const value = trimString(gstin) || trimString(gstNumber);
  return value ? value.toUpperCase() : undefined;
};

const compactPocContact = contact => {
  if (!contact) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(contact).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

/**
 * Build API poc_vendor / poc_client object from discrete fields.
 */
export const buildPocContactPayload = ({
  name,
  role,
  mobile,
  email,
  gstin,
  gst_number,
} = {}) =>
  compactPocContact({
    name: trimString(name) || undefined,
    role: trimString(role) || undefined,
    mobile: trimString(mobile) || undefined,
    email: trimString(email) || undefined,
    gstin: normalizeGstin(gstin, gst_number),
  });

export const buildPocVendorFromForm = formData =>
  buildPocContactPayload({
    name: formData?.pocVendorName,
    role: formData?.pocVendorRole,
    mobile: formData?.pocVendorPhone,
    email: formData?.pocVendorEmail,
    gstin: formData?.pocVendorGstin,
  });

export const buildPocClientFromForm = formData =>
  buildPocContactPayload({
    name: formData?.pocClientName,
    role: formData?.pocClientRole,
    mobile: formData?.pocClientPhone,
    email: formData?.pocClientEmail,
    gstin: formData?.pocClientGstin,
  });

/**
 * Read gstin from API response (gst_number alias supported on read).
 */
export const resolvePocGstin = poc =>
  trimString(poc?.gstin) || trimString(poc?.gst_number) || '';

/**
 * Map API poc object to form field prefix (pocVendor / pocClient).
 */
export const mapPocContactToForm = (poc, prefix) => {
  if (!poc || !prefix) return {};
  return {
    [`${prefix}Name`]: poc.name || '',
    [`${prefix}Role`]: poc.role || '',
    [`${prefix}Phone`]: poc.mobile || '',
    [`${prefix}Email`]: poc.email || '',
    [`${prefix}Gstin`]: resolvePocGstin(poc),
  };
};
